import { trackFrameGift, type FrameGiftOffer } from '@src/utils/frameGiftAnalytics';
import { isDotCom } from '@src/site';
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, GiftIcon, Link, Typography, showDialog } from '@src/components';
import { ThemeArtwork } from '@src/theme/Artwork';
import { useTranslation } from 'react-i18next';
import './GifteeGiftOffer.css';

/** Presentation only: the caller owns checkout and gift-sent actions. */
export function GifteeGiftOffer({ name, offer, onGift, onSent, sentGift, dismissed = false, busy = false, disabled = false }: {
  name: string;
  offer?: FrameGiftOffer | null;
  dismissed?: boolean;
  sentGift?: {sentAt?: string | null; openedAt?: string | null; message: string | null; reply: string | null; thankedAt: string | null} | null;
  onGift?: (flowId: string) => void;
  onSent?: () => void;
  busy?: boolean;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const sentTitle = sentGift?.openedAt ? 'game.ward.personalGiftDelivered' : 'game.ward.personalGiftSent';
  const formatDate = (date: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long', timeStyle: 'short' }).format(new Date(date));
  const Panel = sentGift ? 'button' : 'section';
  const showSentGift = () => {
    if (!sentGift) return;
    showDialog({ title: t(sentTitle), testId: 'personal-gift-details', submit: t('game.ward.frameHelpClose'), children: <div className="giftee-frame-preview">
      <ThemeArtwork role="gift" className="giftee-gift-details__art" />
      <Typography as="p" role="body/base">{t('game.ward.personalGiftDetails', { name })}</Typography>
      {sentGift.message && <div className="giftee-gift-details__message"><Typography as="p" role="meta/helper">{t('game.ward.personalGiftYourMessage')}</Typography><blockquote>{sentGift.message}</blockquote></div>}
      <div className="giftee-gift-details__timestamps">
        <Badge tone="info">{sentGift.sentAt ? t('game.ward.personalGiftSentDate', { date: formatDate(sentGift.sentAt) }) : t('game.ward.personalGiftSentDateUnknown')}</Badge>
        <Badge tone={sentGift.openedAt ? 'success' : 'neutral'}>{sentGift.openedAt ? t('game.ward.personalGiftDeliveredDate', { date: formatDate(sentGift.openedAt) }) : t('game.ward.personalGiftNotOpened')}</Badge>
      </div>
    </div> });
  };
  const [flowId] = useState(() => crypto.randomUUID());
  const offerTracked = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const [hasAppeared, setHasAppeared] = useState(false);
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || sentGift || disabled || !offer) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHasAppeared(true);
        if (!offerTracked.current) {
          offerTracked.current = true;
          trackFrameGift('gift_frame_offer_viewed', { button_variant: dismissed ? 'secondary' : 'primary', flow_id: flowId, test_price: offer?.testPrice }, offer);
        }
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(panel);
    return () => observer.disconnect();
  }, [sentGift, disabled, dismissed, flowId, offer]);
  return (
    <div ref={panelRef} className="giftee-gift-offer" data-appeared={hasAppeared && !sentGift} data-testid="giftee-gift-offer">
      <Panel className="giftee-gift-offer__panel" {...(sentGift ? { type: 'button' as const, onClick: showSentGift, 'data-testid': 'giftee-gift-details' } : {})} aria-label={t(sentGift ? sentTitle : 'game.ward.giftOfferTitle', { name })}>
        <ThemeArtwork role="gift" className="giftee-gift-offer__art" />
        <span className="giftee-gift-offer__copy">
          <Typography as={sentGift ? "span" : "h3"} role="body/sm"><strong>{t(sentGift ? sentTitle : 'game.ward.giftOfferTitle', { name })}</strong></Typography>
          {!sentGift && <Typography as="p" role="body/sm">{t(isDotCom() ? 'game.ward.giftOfferDescriptionCom' : 'game.ward.giftOfferDescription')}</Typography>}
        </span>
        {!sentGift && <Button fullWidth variant={dismissed ? "secondary" : "primary"} icon={<GiftIcon />} onClick={() => { trackFrameGift('gift_frame_offer_clicked', { button_variant: dismissed ? 'secondary' : 'primary', flow_id: flowId, test_price: offer?.testPrice }, offer); onGift?.(flowId); }} disabled={disabled || busy} testId="giftee-gift-offer-cta">
          {t('game.ward.giftOfferAction')}
        </Button>}
      </Panel>
      {onSent && <div className="giftee-gift-offer__sent">
        <Typography as="p" role="meta/helper">{t('game.ward.giftOfferSentHint')}</Typography>
        {(sentGift || dismissed) ? <Button fullWidth variant="primary" onClick={onSent} loading={busy} disabled={disabled} testId="game-ward-sent">{t('game.ward.giftOfferSentAction')}</Button> : <Link onClick={() => { if (!busy && !disabled) onSent(); }} variant="inline" testId="game-ward-sent">{t('game.ward.giftOfferSentAction')}</Link>}
      </div>}
    </div>
  );
}
